import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    ChevronLeft,
    ChevronRight,
    MapPin,
    Calendar,
    Facebook,
    X,
} from "lucide-react";

export default function ProjectDialog({ project, open, onOpenChange }) {
    const images =
        project?.images?.length > 0
            ? project.images
            : [{ url: project?.image, caption: "" }];
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (open) setIdx(0);
    }, [open, project?.id]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "ArrowLeft") prev();
            if (e.key === "ArrowRight") next();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, images.length]);

    if (!project) return null;

    const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
    const next = () => setIdx((i) => (i + 1) % images.length);

    const hasMultiple = images.length > 1;
    const current = images[idx];

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={`project-dialog-${project.id}`}
                className="max-w-5xl w-[95vw] p-0 gap-0 bg-white overflow-hidden max-h-[92vh] flex flex-col sm:rounded-md border-0"
            >
                <DialogTitle className="sr-only">{project.title}</DialogTitle>

                {/* Image area */}
                <div className="relative bg-black">
                    <div className="relative aspect-[16/10] sm:aspect-[16/9] overflow-hidden">
                        <img
                            src={current.url}
                            alt={current.caption || project.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[var(--ns-blue)] text-[11px] font-bold tracking-widest uppercase">
                                {project.categoryLabel}
                            </span>
                            {project.status === "in-progress" && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--ns-blue)] text-white text-[11px] font-bold tracking-widest uppercase">
                                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                    Igangværende
                                </span>
                            )}
                        </div>
                        {hasMultiple && (
                            <>
                                <button
                                    type="button"
                                    onClick={prev}
                                    data-testid="project-dialog-prev"
                                    aria-label="Forrige billede"
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--ns-text)] grid place-items-center shadow-lg transition"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={next}
                                    data-testid="project-dialog-next"
                                    aria-label="Næste billede"
                                    className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[var(--ns-text)] grid place-items-center shadow-lg transition"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-4">
                                    {current.caption && (
                                        <div className="bg-black/60 backdrop-blur text-white text-sm sm:text-base font-medium px-4 py-2 rounded-md max-w-md">
                                            {current.caption}
                                        </div>
                                    )}
                                    <div className="ml-auto inline-flex items-center gap-1.5 bg-black/60 backdrop-blur text-white text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full">
                                        {idx + 1} / {images.length}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        data-testid="project-dialog-close"
                        aria-label="Luk"
                        className="absolute top-3 right-3 w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 text-white grid place-items-center transition"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Thumbnails */}
                {hasMultiple && (
                    <div className="flex gap-2 p-3 bg-[var(--ns-bg)] border-b border-[var(--ns-border)] overflow-x-auto">
                        {images.map((im, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => setIdx(i)}
                                data-testid={`project-dialog-thumb-${i}`}
                                className={`relative flex-shrink-0 w-20 h-14 sm:w-24 sm:h-16 rounded-sm overflow-hidden border-2 transition ${
                                    i === idx
                                        ? "border-[var(--ns-blue)] ring-2 ring-[var(--ns-blue)]/20"
                                        : "border-transparent opacity-60 hover:opacity-100"
                                }`}
                            >
                                <img
                                    src={im.url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                            </button>
                        ))}
                    </div>
                )}

                {/* Body */}
                <div className="p-6 sm:p-8 overflow-y-auto">
                    <h2
                        data-testid="project-dialog-title"
                        className="font-display font-black text-2xl sm:text-3xl tracking-tight text-[var(--ns-text)] mb-3"
                    >
                        {project.title}
                    </h2>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[var(--ns-muted)] mb-5 font-medium">
                        <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-4 h-4" /> {project.location}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-4 h-4" /> {project.year}
                        </span>
                    </div>
                    <div className="prose prose-sm max-w-none">
                        {project.longDescription
                            ?.split("\n")
                            .filter(Boolean)
                            .map((para, i) => (
                                <p
                                    key={i}
                                    className="text-[var(--ns-text)] text-base leading-relaxed mb-3 last:mb-0 whitespace-pre-wrap"
                                >
                                    {para}
                                </p>
                            ))}
                    </div>
                    {project.facebookUrl && (
                        <a
                            href={project.facebookUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="project-dialog-facebook"
                            className="mt-6 inline-flex items-center gap-2 btn-primary !py-3 !px-5 text-sm"
                        >
                            <Facebook className="w-4 h-4" />
                            Se opslag på Facebook
                        </a>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
